import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getAuth, clearAuth, isSuperAdmin } from '../lib/auth';
import { AccountSwitcher } from './AccountSwitcher';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LayoutDashboard, FileText, Download, Settings as SettingsIcon, LogOut, Shield, Landmark, MapPin, Box, Gauge, Building, Building2, ChevronDown, CreditCard, Receipt, BarChart3, BadgeDollarSign, ArrowUpDown, Zap, AlertOctagon } from 'lucide-react';

export const MainLayout = ({ children, wide = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = getAuth();
  
  // Use state to track current account so we can update it when AccountSwitcher refreshes data
  const [currentAccount, setCurrentAccountState] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  // Re-check localStorage periodically for account updates from AccountSwitcher
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) {
        setCurrentAccountState(acc);
      }
    };
    
    // Check on mount and every second for 10 seconds (to catch AccountSwitcher updates)
    checkAccount();
    const interval = setInterval(checkAccount, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname]); // Re-run on navigation
  
  // Check if UBM is enabled for current account
  const isUbmEnabled = currentAccount?.enabled_apps?.ubm === true;
  const isUbmRoute = location.pathname.startsWith('/ubm');
  
  // Check if Payments is enabled for current account
  const isPaymentsEnabled = currentAccount?.enabled_apps?.payments === true;
  const isPaymentsRoute = location.pathname.startsWith('/payments');
  const isBillsRoute = location.pathname.startsWith('/bills');
  
  // IntakeHub: only visible if enabled_apps.intake_hub is explicitly enabled for the org
  const isIntakeHubEnabled = currentAccount?.enabled_apps?.intake_hub === true;

  const handleLogout = () => {
    clearAuth();
    window.location.href = '/login';
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-8 w-8" />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {isSuperAdmin() && (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none"
                onClick={() => navigate('/super-admin')}
                data-testid="super-admin-link"
              >
                <Shield className="h-4 w-4 mr-2" />
                Super Admin
              </Button>
            )}
            <AccountSwitcher />
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button
              data-testid="settings-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/settings')}
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Horizontal Navigation Menu */}
      <nav className="border-b border-border bg-card">
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-1">
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </NavLink>
            {isIntakeHubEnabled && (
              <NavLink to="/bill-jobs" className={navLinkClass} data-testid="nav-bill-jobs">
                <FileText className="h-4 w-4" />
                Intake Hub
              </NavLink>
            )}
            {/* Bulk Downloads hidden — uncomment when needed
            <NavLink to="/bulk-downloads" className={navLinkClass} data-testid="nav-bulk-downloads">
              <Download className="h-4 w-4" />
              Bulk Downloads
            </NavLink>
            */}
            
            {/* Bills - Dropdown with Bills List + Dashboard (requires payments enabled) */}
            {isPaymentsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isBillsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    } rounded-md`}
                    data-testid="nav-bills-dropdown"
                  >
                    <Receipt className="h-4 w-4" />
                    Bills
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bills')}
                    data-testid="nav-bills-list"
                  >
                    <Receipt className="h-4 w-4 mr-2" /> Bills List
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bills/dashboard')}
                    data-testid="nav-bills-dashboard"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" /> Bills Dashboard
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Payments Dropdown - Only show if enabled for org */}
            {isPaymentsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isPaymentsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-payments"
                  >
                    <CreditCard className="h-4 w-4" />
                    Payments
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/dashboard')}
                    className="cursor-pointer"
                    data-testid="nav-payments-dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments')}
                    className="cursor-pointer"
                    data-testid="nav-payments-files"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Payment Files
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/bills')}
                    className="cursor-pointer"
                    data-testid="nav-payments-bills"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    PF Bills
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/service-fees')}
                    className="cursor-pointer"
                    data-testid="nav-payments-service-fees"
                  >
                    <BadgeDollarSign className="h-4 w-4 mr-2" />
                    Service Fee Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/variance')}
                    className="cursor-pointer"
                    data-testid="nav-payments-variance"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Variance Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/credit-debit')}
                    className="cursor-pointer"
                    data-testid="nav-payments-credit-debit"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Credit / Debit Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/emergency-bill-upload')}
                    className="cursor-pointer"
                    data-testid="nav-payments-emergency-bill-upload"
                  >
                    <AlertOctagon className="h-4 w-4 mr-2" />
                    Emergency Paid Bill Upload
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* UBM Dropdown - Next to Payments */}
            {isUbmEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isUbmRoute 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-ubm"
                  >
                    <Landmark className="h-4 w-4" />
                    UBM Onboarding
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  <DropdownMenuItem onClick={() => navigate('/ubm/properties')} className="cursor-pointer">
                    <MapPin className="h-4 w-4 mr-2" /> Properties
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/units')} className="cursor-pointer">
                    <Box className="h-4 w-4 mr-2" /> Units
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/meters')} className="cursor-pointer">
                    <Gauge className="h-4 w-4 mr-2" /> Meters
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/vendors')} className="cursor-pointer">
                    <Building className="h-4 w-4 mr-2" /> Vendor Accounts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/bill2rollin')} className="cursor-pointer" data-testid="nav-ubm-bill2rollin">
                    <Zap className="h-4 w-4 mr-2" /> AI Bill2Rollin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main className="px-4 md:px-6 py-6">
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
